import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $profileFeatureStore } from '@lib/store';
import { $usersStore } from '@lib/store/userManagement';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import type { CreateUserRequest, UsersRequest } from '@shared/api/rest/users';
import { Button, Icon, NoData, PageContainer, Pagination, Spinner } from '@shared/widgets';
import { AddNewUser, SuccessDeletedUserModal, User, UserListView } from './widgets';
import PlusIcon from '@assets/icons/plus.svg';
import GridViewIcon from '@assets/icons/grid-view.svg';
import LineViewIcon from '@assets/icons/line-view.svg';

export const UserManagementPage = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const usersStore = useUnit($usersStore.store);

  const [index, setIndex] = useState<number>(1);
  const [count, setCount] = useState<number>(10);
  const [viewChange, setViewChange] = useState<boolean>(true);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showSuccessDeletedUserModal, setShowSuccessDeletedUserModal] = useState<boolean>(false);

  const { navigate } = useRouteHook();

  useEffect(() => {
    if (!profileFeatureStore.data?.userManagement.viewUsers) navigate(RoutePaths.dashboard.onNavigate());
  }, []);

  const params: UsersRequest = useMemo(
    () => ({
      FromIndex: (index - 1) * count,
      Count: count,
      ReturnTotalCount: true,
    }),
    [index, count],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const show = searchParams.get('successDeleted');
    setShowSuccessDeletedUserModal(show === 'true');
    const params = new URLSearchParams(searchParams);
    params.delete('successDeleted');
    setSearchParams(params);
  }, []);

  useEffect(() => {
    if (profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions) {
      $usersStore.events.getUsers(params);
    } else {
      $usersStore.events.getUsersWithoutPermission(params);
    }
  }, [index, count]);

  const addNewUserHandler = useCallback(
    async (user: CreateUserRequest) => {
      await $usersStore.events.addUser({ user, params });
    },
    [params],
  );

  if (!profileFeatureStore.data?.userManagement.viewUsers) return null;
  return (
    <PageContainer
      title='User Management'
      header={'Users (' + usersStore.data.data.length + ')'}
      topRightAddons={
        <div className='flex items-center'>
          {profileFeatureStore.data?.userManagement.createUser && (
            <Button className='mr-2' color='primary' onClick={() => setShowAddModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              Add
            </Button>
          )}
          <Button color='icon' onClick={() => setViewChange(true)}>
            <Icon icon={<GridViewIcon />} className={clsx(viewChange && '!text-primary')} />
          </Button>
          <Button className='ml-4' color='icon' onClick={() => setViewChange(false)}>
            <Icon icon={<LineViewIcon />} className={clsx(!viewChange && '!text-primary')} />
          </Button>
        </div>
      }
    >
      {usersStore.loading && <Spinner position />}
      {usersStore.initialLoading ? (
        <Spinner />
      ) : (
        <>
          {profileFeatureStore.data?.userManagement.viewUsers && (
            <div className={clsx('grid gap-4 mb-6', !viewChange ? 'grid-cols-1' : 'grid-cols-3')}>
              {viewChange && usersStore.data.data.map((item, i) => <User key={i} user={item} />)}
              {!viewChange && <UserListView users={usersStore.data.data} />}
            </div>
          )}
          {usersStore.data.data.length === 0 ? <NoData /> : null}
        </>
      )}
      <Pagination
        currentPage={index}
        totalItemCount={usersStore.data.total}
        countItemPage={count}
        onChangePage={setIndex}
        onChangeCountItemPage={setCount}
      />
      <AddNewUser show={showAddModal} onClose={() => setShowAddModal(false)} addNewUser={addNewUserHandler} />
      <SuccessDeletedUserModal show={showSuccessDeletedUserModal} onClose={() => setShowSuccessDeletedUserModal(false)} />
    </PageContainer>
  );
};
